import {
  Alert,
  Button,
  CardContent,
  Chip,
  Divider,
  FormControlLabel,
  List,
  ListItemButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { EFSRestoreDestinationRestoreTypeEnum } from '@repo/api-gw-sdk';
import { EFSRestoreDestinationRestoreTypeEnum as RestoreTypeEnum } from '@repo/api-gw-sdk';
import { useState, useCallback } from 'react';

import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import type { StepProps } from '@/components/wizard/StepProps';

import { StepContainer } from '../../wizard/StepContainer';
import type { RestoreEFSState } from '../restoreEfsWizard';

const RESTORE_TYPE_OPTIONS: {
  value: EFSRestoreDestinationRestoreTypeEnum;
  title: string;
  description: string;
}[] = [
  {
    value: RestoreTypeEnum.FullRestore,
    title: 'Full restore',
    description: 'Restore the entire file system to a new EFS file system.',
  },
  {
    value: RestoreTypeEnum.ItemLevelRestore,
    title: 'Item-level restore',
    description:
      'Restore specific files or directories to the source or a new file system.',
  },
];

export const EFSRestoreTypeSelection = (props: StepProps<RestoreEFSState>) => {
  const [newItemPath, setNewItemPath] = useState('');

  const isItemLevel =
    props.wizardState.restoreType === RestoreTypeEnum.ItemLevelRestore;
  const isNewFS = props.wizardState.restoreToNewFileSystem;
  const hasSourceAccount = !!props.wizardState.restoreAccount;

  const efsProps = props.wizardState.snapshot.efsSnapshotProperties;
  const awsBackupVaultInfo = efsProps
    ? {
        cloudProvider: props.wizardState.supportedCloudProviders[0],
        region: efsProps.region || props.wizardState.initialRegionName,
      }
    : undefined;

  const addItemPath = useCallback(() => {
    const trimmed = newItemPath.trim();
    if (!trimmed) return;
    const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    if (props.wizardState.itemsToRestore.includes(path)) return;
    props.setWizardState((state) => ({
      ...state,
      itemsToRestore: [...state.itemsToRestore, path],
    }));
    setNewItemPath('');
  }, [newItemPath, props]);

  const removeItemPath = useCallback(
    (path: string) => {
      props.setWizardState((state) => ({
        ...state,
        itemsToRestore: state.itemsToRestore.filter((p) => p !== path),
      }));
    },
    [props]
  );

  const canGoNext = () => {
    if (!hasSourceAccount) return false;
    if (isItemLevel && props.wizardState.itemsToRestore.length === 0)
      return false;
    return true;
  };

  const onRestoreTypeClicked = (
    value: EFSRestoreDestinationRestoreTypeEnum
  ) => {
    props.setWizardState((state) => ({
      ...state,
      restoreType: value,
      restoreToNewFileSystem:
        value === RestoreTypeEnum.FullRestore
          ? true
          : state.restoreToNewFileSystem,
    }));
  };

  return (
    <StepContainer
      stepperLabels={props.stepperLabels}
      stepperIndex={0}
      onBackClick={props.back}
      canGoNext={canGoNext}
      onNextClick={async () => props.setNextStep(props.currentStep.next?.[0])}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
          supportPiTR={props.wizardState.snapshot.supportPiTR}
          awsBackupVaultInfo={awsBackupVaultInfo}
        />
      }
    >
      {!hasSourceAccount && (
        <Alert severity='info' className='mb-[20px]'>
          <Typography variant='body2'>
            EFS backups are stored in AWS Backup Vault in the source account and
            can only be restored to the same account.
          </Typography>
        </Alert>
      )}

      <Typography component='span'>Select a restore type:</Typography>
      <List className='mt-[20px] mb-[10px]'>
        {RESTORE_TYPE_OPTIONS.map((option) => {
          const isChecked = props.wizardState.restoreType === option.value;
          return (
            <ListItemButton
              key={option.value}
              sx={{
                border: isChecked
                  ? '2px solid var(--mui-palette-primary-main)'
                  : '1px solid var(--mui-palette-divider)',
                padding: isChecked ? '16px 24px' : '17px 25px',
                marginBottom: '8px',
              }}
              onClick={() => onRestoreTypeClicked(option.value)}
              disabled={!hasSourceAccount}
            >
              <Radio className='p-0' checked={isChecked} value={option.value} />
              <Stack className='ml-[16px]'>
                <Typography variant='body1'>{option.title}</Typography>
                <Typography variant='body2' color='text.secondary'>
                  {option.description}
                </Typography>
              </Stack>
            </ListItemButton>
          );
        })}
      </List>

      {isItemLevel && (
        <>
          <Divider />
          <CardContent className='px-0'>
            <Stack gap='12px'>
              <Typography className='font-semibold' variant='body1'>
                Items to restore
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Specify file or directory paths relative to the file system
                root. Paths are case-sensitive and must start with
                &apos;/&apos;.
              </Typography>
              <Stack direction='row' gap='12px' alignItems='flex-start'>
                <TextField
                  data-testid='itemPathTextField'
                  size='small'
                  placeholder='/path/to/file-or-directory'
                  value={newItemPath}
                  onChange={(e) => setNewItemPath(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addItemPath();
                    }
                  }}
                  className='flex-1'
                  disabled={!hasSourceAccount}
                />
                <Button
                  variant='outlined'
                  size='small'
                  onClick={addItemPath}
                  disabled={!hasSourceAccount || !newItemPath.trim()}
                  sx={{ minWidth: '100px' }}
                >
                  Add item
                </Button>
              </Stack>
              {props.wizardState.itemsToRestore.length > 0 && (
                <Stack direction='row' gap='12px' flexWrap='wrap'>
                  {props.wizardState.itemsToRestore.map((path) => (
                    <Chip
                      key={path}
                      label={path}
                      onDelete={() => removeItemPath(path)}
                      variant='outlined'
                      size='small'
                    />
                  ))}
                </Stack>
              )}
              {props.wizardState.itemsToRestore.length === 0 && (
                <Alert severity='warning' variant='outlined'>
                  <Typography variant='body2'>
                    Add at least one file or directory path to restore.
                  </Typography>
                </Alert>
              )}
            </Stack>
          </CardContent>

          <Divider />
          <CardContent className='px-0'>
            <Stack gap='12px'>
              <Typography className='font-semibold' variant='body1'>
                Restore destination
              </Typography>
              <RadioGroup
                value={isNewFS ? 'new' : 'existing'}
                onChange={(e) =>
                  props.setWizardState((state) => ({
                    ...state,
                    restoreToNewFileSystem: e.target.value === 'new',
                  }))
                }
              >
                <FormControlLabel
                  value='existing'
                  control={<Radio size='small' />}
                  label='Restore to source file system'
                  disabled={!hasSourceAccount}
                />
                <FormControlLabel
                  value='new'
                  control={<Radio size='small' />}
                  label='Restore to a new file system'
                  disabled={!hasSourceAccount}
                />
              </RadioGroup>
              {!isNewFS && (
                <Alert severity='info' variant='outlined'>
                  <Typography variant='body2'>
                    Items will be restored to a new timestamped directory in the
                    source file system, preserving the original hierarchy.
                  </Typography>
                </Alert>
              )}
            </Stack>
          </CardContent>
        </>
      )}
    </StepContainer>
  );
};
